import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  getCampaignDetailsDiscountLabel,
  getCampaignDetailsExpirationLabel
} from '@/ducks/campaigns/campaigns.helpers'
import type { CampaignItemDetail, FoodOfferCampaign } from '@/ducks/campaigns/campaigns.types'
import {
  findFoodOfferMenuItem,
  getFoodOfferCampaignFallbackExpiration,
  getFoodOfferDiscountLabel,
  getFoodOfferExpirationText,
  getFoodOfferFoodId,
  mapFoodOfferMenuItemToCampaignDetail
} from '@/ducks/campaigns/foodOfferCampaign.helpers'
import { useFetchOffersQuery } from '@/ducks/offers/api/offers.api'
import { selectCurrencySymbol } from '@/ducks/store/selectors/store.selectors'
import { useServerTranslation } from '@/hooks/useServerTranslation/useServerTranslation'

export type FoodOfferCampaignDetails = {
  isLoading: boolean
  isError: boolean
  itemDetail?: CampaignItemDetail
  discountLabel: string
  expirationLabel: string
  foodId: string
}

const useFoodOfferCampaignDetails = (
  campaign: FoodOfferCampaign | null,
  enabled: boolean
): FoodOfferCampaignDetails | null => {
  const { t } = useTranslation()
  const { getTranslation } = useServerTranslation()
  const currency = useSelector(selectCurrencySymbol) || ''
  const foodId = campaign ? getFoodOfferFoodId(campaign) : ''

  const { data, isFetching, isError } = useFetchOffersQuery(undefined, {
    skip: !enabled || !campaign
  })

  return useMemo(() => {
    if (!campaign) return null

    const found = findFoodOfferMenuItem(data, foodId, getTranslation)

    if (found) {
      // The menu item says how its discount works; the list row is the fallback.
      const discountType =
        found.item.offer?.discount_type === 'flat'
          ? 'flat'
          : campaign.offerDiscountType
      return {
        isLoading: isFetching,
        isError,
        itemDetail: mapFoodOfferMenuItemToCampaignDetail(
          found.item,
          found.categoryPath,
          getTranslation,
          discountType
        ),
        discountLabel:
          getFoodOfferDiscountLabel(found.item, t, currency, discountType) ??
          getCampaignDetailsDiscountLabel(campaign, t, currency),
        expirationLabel: getFoodOfferExpirationText(
          found.item,
          t,
          undefined,
          discountType
        ),
        foodId
      }
    }

    return {
      isLoading: isFetching,
      isError,
      itemDetail: campaign.item,
      discountLabel: getCampaignDetailsDiscountLabel(campaign, t, currency),
      expirationLabel: getFoodOfferCampaignFallbackExpiration(campaign, t),
      foodId
    }
  }, [campaign, currency, data, foodId, getTranslation, isError, isFetching, t])
}

export default useFoodOfferCampaignDetails
